import React, { useEffect, useMemo, useState } from "react";
import { IntegrationService } from "../api";
import { Box, Checkbox, Chip, CircularProgress, FormControl, FormControlLabel, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField, Typography, Accordion, AccordionSummary, AccordionDetails, Button, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { CardComponent } from "components/card/Card";
import { useNavigate } from "react-router";
import { CreateIntellifloTenantMutation, CreateIntellifloTenantMutationVariables, UpdateIntellifloTenantMutation } from "../api/intelliflo-integration.generated";
import { enqueueSnackbar } from "notistack";
import { FieldArray, Formik, FormikErrors, useFormikContext } from "formik";
import { useGetAllIoConfigQuery } from "core/scenes/io-config/api/io-config.generated";
import { CreateIntellifloTenantInput, IntellifloIntegrationLegalType, LegalEntitiesType } from "app/services/types.generated";
import { useAuth } from "app/authHooks";
import { hasPermission, Permission } from "core/utils/roles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BlockIcon from "@mui/icons-material/Block";
import { useTheme } from "@mui/material/styles";
import { CheckBox, DisabledByDefault } from "@mui/icons-material";

interface EditIntegrationProps {
  formValue: IntegrationService.CombinedSchemaType,
  isEdit: boolean,
  id?: string
}

enum IntellifloTenantStatus {
  NEW = "NEW",
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

type StatusDisplayConfig = {
  helper: string;
  textColor: string;
  dotColor: string;
  label: string;
};

const validateTenantStatus = (status?: string | null): IntellifloTenantStatus | undefined => {
  if (status === null || status === undefined) {
    return IntellifloTenantStatus.ACTIVE;
  }
  if (typeof status !== "string") {
    return IntellifloTenantStatus.ACTIVE;
  }
  const trimmedStatus = status.trim();
  if (!trimmedStatus) {
    return IntellifloTenantStatus.ACTIVE;
  }
  const upperCasedStatus = trimmedStatus.toUpperCase();
  if ((Object.values(IntellifloTenantStatus) as string[]).includes(upperCasedStatus)) {
    return upperCasedStatus as IntellifloTenantStatus;
  }
  return undefined;
};


function EditIntegration({ formValue, isEdit, id }: EditIntegrationProps) {
  const { attributes } = useAuth();
  const navigate = useNavigate();

  const canUpdateIOtenant = hasPermission("update:io-integrations" as Permission);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [confirmationValue, setConfirmationValue] = useState("");
  const [confirmationStatus, setConfirmationStatus] = useState<IntellifloTenantStatus | undefined>(undefined);
  const theme = useTheme();
  const statusDisplayConfig = useMemo<Record<IntellifloTenantStatus, StatusDisplayConfig>>(() => {
    const warningColor = theme.palette.warning ? theme.palette.warning.main : "#ed6c02";
    const successColor = theme.palette.success ? theme.palette.success.main : "#2e7d32";
    const errorColor = theme.palette.error ? theme.palette.error.main : "#d32f2f";

    return {
      [IntellifloTenantStatus.NEW]: {
        helper: "NEW",
        textColor: warningColor,
        dotColor: warningColor,
        label: IntellifloTenantStatus.NEW,
      },
      [IntellifloTenantStatus.ACTIVE]: {
        helper: "ACTIVE",
        textColor: successColor,
        dotColor: successColor,
        label: IntellifloTenantStatus.ACTIVE,
      },
      [IntellifloTenantStatus.INACTIVE]: {
        helper: "INACTIVE",
        textColor: errorColor,
        dotColor: errorColor,
        label: IntellifloTenantStatus.INACTIVE,
      },
    };
  }, [theme]);

  const [createIntellifloTenant] = IntegrationService.useCreateIntellifloTenantMutation();
  const [updateIntellifloTenant] = IntegrationService.useUpdateIntellifloTenantMutation();
  const [disableOrDeleteTenant, { isLoading: isDisableOrDeleteLoading }] = IntegrationService.useDisableOrDeleteIntellifloTenantMutation();
  const [toggleStatus] = IntegrationService.useToggleLegalEntityStatusMutation();

  const { data: ioConfigs } = useGetAllIoConfigQuery({ limit: 1000 });
  const ioConfigList = ioConfigs?.getAllIoConfig?.data?.data || [];

  const enum LegalEntityStatus {
    ACTIVE = "ACTIVE",
    INACTIVE = "INACTIVE"
  }

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [stopIntegration, setStopIntegration] = React.useState(true);
  const [selectedEntity, setSelectedEntity] = React.useState<{ customerId: string; status: string | null | undefined; tenantId: string; } | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const toggleLegalEntityStatus = async (tenantId: string, customerId: string, status: LegalEntityStatus, toggleIntegrationConnection: boolean) => {
    setIsLoading(true);
    try {
      const response = await toggleStatus({
        input: {
          tenantId,
          customerId,
          status,
          toggleIntegrationConnection,
        }
      }).unwrap();

      if (!response.toggleLegalEntityStatus.success) {
        throw new Error(response.toggleLegalEntityStatus.errorMessage);
      }
      enqueueSnackbar(`Successfully changed status for ${customerId} entity`, { variant: "success" });
    } catch (error) {
      enqueueSnackbar(`Failed to change status for ${customerId} entity`, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitForm = async (values: IntegrationService.CombinedSchemaType) => {
    if (values?.intellifloIntegrationLegalEntitiesInput?.length < 1) {
      enqueueSnackbar("Please insert at least one legal entities.", {
        variant: "error", anchorOrigin: {
          vertical: "top",
          horizontal: "right"
        }
      });
      return;
    }

    if (isEdit) {
      const updateResponse = await updateIntellifloTenant({
        input: {
          integrationAndLegalEntitiesInput: values.intellifloIntegrationLegalEntitiesInput,
          intellifloTenantInput: values.intellifloTenantInput,
        }
      }) as { data: UpdateIntellifloTenantMutation };
      if (updateResponse.data.updateIntellifloTenant.success) {
        enqueueSnackbar("Intelliflo Tenant Updated Successfully.", { variant: "success" });
        navigate("/integrations");
      } else {
        enqueueSnackbar("Error Updating Intelliflo Tenant.", { variant: "error" });
      }
      return;
    }

    const createIntellifloTenantData: CreateIntellifloTenantMutationVariables = {
      input: {
        intellifloTenantInput: values.intellifloTenantInput,
        integrationAndLegalEntitiesInput: values.intellifloIntegrationLegalEntitiesInput,
      },
    };

    const createResponse = await createIntellifloTenant(createIntellifloTenantData) as { data: CreateIntellifloTenantMutation };

    if (createResponse?.data?.createIntellifloTenant?.success) {
      enqueueSnackbar("Intelliflo Tenant Added Successfully", { variant: "success" });
      navigate("/integrations");
    } else {
      enqueueSnackbar(createResponse?.data?.createIntellifloTenant?.errorMessage || "Error Creating Intelliflo Tenant", { variant: "error" });
    }
    return;
  };

  return (
    <>
      <Formik
        onSubmit={handleSubmitForm}
        initialValues={formValue}
        validationSchema={IntegrationService.CombinedSchema}
      >
        {({
          values,
          errors,
          handleChange,
          handleSubmit,
          touched,
          handleBlur
        }) => {
          const tenantStatus = validateTenantStatus(values.intellifloTenantInput?.metadata?.status);
          const isTenantInactive = tenantStatus === IntellifloTenantStatus.INACTIVE;
          const canModifyTenant = canUpdateIOtenant && !isTenantInactive;
          const isReadOnly = isEdit || isTenantInactive;
          const statusConfig = statusDisplayConfig[tenantStatus ?? IntellifloTenantStatus.ACTIVE];
          const tenantId = values.intellifloTenantInput?.externalId || "";
          const handleOpenConfirmationDialog = () => {
            setConfirmationStatus(tenantStatus);
            setConfirmationValue("");
            setIsConfirmDialogOpen(true);
          };
          const handleCloseConfirmationDialog = () => {
            setIsConfirmDialogOpen(false);
            setConfirmationValue("");
          };
          const dialogStatus = confirmationStatus ?? tenantStatus;
          const disablePhrase = `disable ${tenantId}`.trim();
          const deletePhrase = `delete ${tenantId}`.trim();
          const expectedPhrase = dialogStatus === IntellifloTenantStatus.ACTIVE
            ? disablePhrase
            : deletePhrase;
          const isConfirmationValid = expectedPhrase.length > 0 && confirmationValue.trim().toLowerCase() === expectedPhrase.toLowerCase();
          const confirmButtonLabel = dialogStatus === IntellifloTenantStatus.ACTIVE ? "Disable Tenant" : "Delete Tenant";
          const actionButtonLabel = (tenantStatus === IntellifloTenantStatus.ACTIVE || !tenantStatus) ? "Disable Tenant" : "Delete Tenant";
          const ActionIcon = tenantStatus === IntellifloTenantStatus.ACTIVE ? BlockIcon : DeleteOutlineIcon;
          const dialogInstruction = dialogStatus === IntellifloTenantStatus.ACTIVE
            ? (
              <>
                Please write <strong>"{disablePhrase}"</strong> below for confirmation of disabling of Tenant.
              </>
            )
            : (
              <>
                Please write <strong>{deletePhrase}</strong> to confirm deletion of Tenant.
              </>
            );
          const handleConfirmAction = async () => {
            if (!tenantId) {
              enqueueSnackbar("Tenant identifier is missing.", { variant: "error" });
              return;
            }

            try {
              const response = await disableOrDeleteTenant({ tenantId }).unwrap();
              const mutationResult = response?.disableOrDeleteIntellifloTenant;

              if (mutationResult?.success) {
                const successMessage = dialogStatus === IntellifloTenantStatus.ACTIVE
                  ? "Triggered Stepfunction to disable tenant successfully."
                  : "Tenant deleted successfully.";
                enqueueSnackbar(successMessage, { variant: "success" });
                handleCloseConfirmationDialog();
                navigate("/integrations");
              } else {
                enqueueSnackbar(mutationResult?.errorMessage || "Unable to update tenant.", { variant: "error" });
              }
            } catch (error) {
              const fallbackMessage = dialogStatus === IntellifloTenantStatus.ACTIVE
                ? "Failed to disable tenant."
                : "Failed to delete tenant.";

              enqueueSnackbar(fallbackMessage, { variant: "error" });
            }
          };

          return <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <CardComponent
                title={isEdit
                  ? canUpdateIOtenant
                    ? "Edit Integration"
                    : "View Integration"
                  : "Add Integration"}
                cardContent={
                  <Box sx={{ "& > *:not(:last-child)": { mb: 8 } }}>
                    <Grid container spacing={2}>
                      <Grid item md={6} xs={10} style={{ textAlign: "left" }}>
                        <FormControl style={{ width: "100%" }}>
                          <InputLabel id="integration-type">Integration Type</InputLabel>
                          <Select
                            name="intellifloTenantInput.type"
                            labelId="integration-type"
                            value={values.intellifloTenantInput?.type}
                            error={!!touched.intellifloTenantInput?.type &&
                              !!errors?.intellifloTenantInput?.type}
                            disabled={isReadOnly}
                            id="integration-type"
                            label="Integration Type"
                            onBlur={handleBlur}
                            onChange={handleChange}
                          >
                            {Object.values(IntegrationService.IntegrationType).map((value, index) => (
                              <MenuItem key={index} value={value}>
                                {value.toUpperCase()}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item md={6} xs={10} style={{ textAlign: "left" }}>
                        <FormControl variant="standard" style={{ width: "100%" }}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            type="text"
                            label="Intelliflo Tenant ID"
                            name="intellifloTenantInput.externalId"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.intellifloTenantInput?.externalId}
                            error={!!touched.intellifloTenantInput?.externalId && !!errors?.intellifloTenantInput?.externalId}
                            helperText={!!touched.intellifloTenantInput?.externalId && errors?.intellifloTenantInput?.externalId}
                            disabled={isReadOnly}
                            sx={{
                              gridColumn: "span 4",
                            }}
                            InputLabelProps={{ shrink: true }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item md={6} xs={10} style={{ textAlign: "left" }}>
                        <FormControl variant="standard" style={{ width: "100%" }}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            type="text"
                            label="Hubwise Customer Identifier"
                            name="intellifloTenantInput.internalId"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.intellifloTenantInput?.internalId}
                            error={!!touched?.intellifloTenantInput?.internalId && !!errors?.intellifloTenantInput?.internalId}
                            helperText={!!touched.intellifloTenantInput?.internalId && errors?.intellifloTenantInput?.internalId}
                            disabled={isReadOnly}
                            sx={{
                              gridColumn: "span 4",
                            }}
                            InputLabelProps={{ shrink: true }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item md={6} xs={10} style={{ textAlign: "left" }}>
                        <FormControl variant="standard" style={{ width: "100%" }}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            type="text"
                            label="Hubwise Customer Name"
                            name="intellifloTenantInput.metadata.displayName"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.intellifloTenantInput?.metadata?.displayName}
                            error={!!touched.intellifloTenantInput?.metadata?.displayName && !!errors?.intellifloTenantInput?.metadata?.displayName}
                            helperText={!!touched.intellifloTenantInput?.metadata?.displayName && errors?.intellifloTenantInput?.metadata?.displayName}
                            disabled={isReadOnly}
                            sx={{
                              gridColumn: "span 4",
                            }}
                            InputLabelProps={{ shrink: true }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item md={6} xs={10} style={{ textAlign: "left" }}>
                        <FormControl style={{ width: "100%" }}>
                          <InputLabel id="provider-app-id">Provider App Id</InputLabel>
                          <Select
                            fullWidth
                            id="provider-app-id"
                            labelId="provider-app-id"
                            label="Provider App Id"
                            placeholder="Provider App Id"
                            name="intellifloTenantInput.providerAppId"
                            value={values.intellifloTenantInput?.providerAppId}
                            error={!!touched.intellifloTenantInput?.providerAppId && !!errors.intellifloTenantInput?.providerAppId}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            disabled={!canModifyTenant }
                          >
                            {ioConfigList?.map((value, index) => (
                              <MenuItem key={index} value={value.appId}>
                                {value.appId}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item md={6} xs={10} style={{ textAlign: "left" }}>
                        <FormControl style={{ width: "100%" }}>
                          <InputLabel id="bulk-val-app-id">Bulk Valuation App Id</InputLabel>
                          <Select
                            fullWidth
                            labelId="bulk-val-app-id"
                            id="bulk-val-app-id"
                            label="Bulk Valuation App Id"
                            placeholder="Bulk Valuation App Id"
                            name="intellifloTenantInput.bulkValAppId"
                            value={values.intellifloTenantInput?.bulkValAppId}
                            error={!!touched.intellifloTenantInput?.bulkValAppId && !!errors.intellifloTenantInput?.bulkValAppId}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            disabled={!canModifyTenant}
                          >
                            {ioConfigList?.map((value, index) => (
                              <MenuItem key={index} value={value.appId}>
                                {value.appId}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      {statusConfig && (
                        <Grid item md={6} xs={10} style={{ textAlign: "left" }}>
                          <FormControl variant="standard" style={{ width: "100%" }}>
                            <Tooltip title={statusConfig.helper} arrow>
                              <TextField
                                fullWidth
                                variant="outlined"
                                label="Status"
                                value={statusConfig.label}
                                disabled
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                  readOnly: true,
                                  startAdornment: (
                                    <InputAdornment position="start" sx={{ mr: 1 }}>
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: "50%",
                                          backgroundColor: statusConfig.dotColor,
                                        }}
                                      />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-input": {
                                    fontWeight: 600,
                                    color: statusConfig.textColor,
                                    WebkitTextFillColor: statusConfig.textColor,
                                  },
                                  "& .MuiOutlinedInput-input.Mui-disabled": {
                                    color: statusConfig.textColor,
                                    WebkitTextFillColor: statusConfig.textColor,
                                  },
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 1,
                                  },
                                  "& .MuiInputAdornment-root .MuiTypography-root": {
                                    color: statusConfig.textColor,
                                    WebkitTextFillColor: statusConfig.textColor,
                                  },
                                }}
                              />
                            </Tooltip>
                          </FormControl>
                        </Grid>
                      )}
                      <Grid item md={6} xs={10} style={{ textAlign: "left" }}>
                        <FormControl style={{ width: "100%" }}>
                          <InputLabel id="employee-creation">Employee Creation</InputLabel>
                          <Select
                            fullWidth
                            labelId="employee-creation"
                            id="employee-creation"
                            label="Employee Creation"
                            placeholder="Employee Creation"
                            name="intellifloTenantInput.employeeCreation"
                            value={values.intellifloTenantInput?.employeeCreation}
                            error={!!touched.intellifloTenantInput?.employeeCreation && !!errors.intellifloTenantInput?.employeeCreation}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            disabled={!canModifyTenant}
                          >
                            <MenuItem value="Hubwise">Hubwise</MenuItem>
                            <MenuItem value="Intelliflo">Intelliflo</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                }
              />


              <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                Legal Entities
              </Typography>
              <Box m={2}>
                <div>
                  <FieldArray
                    name="intellifloIntegrationLegalEntitiesInput"
                    render={arrayHelpers => (
                      <>
                        <Grid container spacing={2}  >
                          {values?.intellifloIntegrationLegalEntitiesInput.map((item, index) => (
                            <Accordion key={index} sx={{ width: "100%", mb: 2 }}>
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`panel${index}-content`}
                                id={`panel${index}-header`}
                              >
                                <Box sx = {{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                  <Typography variant="h5" >
                                    {item.legalEntity.customerId || item.legalEntity.customerName  ? item.legalEntity.customerName + " - " + item.legalEntity.customerId  : "Legal Entity"}
                                  </Typography>
                                  <Chip sx={{ fontWeight: 600, mr: 1 }} label={item.integrationSettings.status ? item.integrationSettings.status : LegalEntityStatus.ACTIVE} color={item.integrationSettings.status === LegalEntityStatus.ACTIVE|| item.integrationSettings.status === null ? "success" : "error"} />
                                </Box>
                              </AccordionSummary>

                              <AccordionDetails>

                                <Grid container spacing={2} key={index} sx={{ mt: -4 }}>

                                  <Grid item container spacing={2} md={12} xs={12} mt="5px">

                                    <Grid item md={3} xs={12}>
                                      <FormControl variant="standard" style={{ width: "100%" }}>
                                        <TextField
                                          fullWidth
                                          variant="outlined"
                                          type="text"
                                          label="Customer Name"
                                          onBlur={handleBlur}
                                          onChange={handleChange}
                                          value={values.intellifloIntegrationLegalEntitiesInput?.[index]?.legalEntity?.customerName}
                                          name={`intellifloIntegrationLegalEntitiesInput[${index}].legalEntity.customerName`}
                                          error={
                                            !!touched?.intellifloIntegrationLegalEntitiesInput?.[index]?.legalEntity?.customerName &&
                                            !!(errors?.intellifloIntegrationLegalEntitiesInput?.[index] as FormikErrors<IntellifloIntegrationLegalType>)?.legalEntity?.customerName
                                          }
                                          helperText={!!touched?.intellifloIntegrationLegalEntitiesInput?.[index]?.legalEntity?.customerName &&
                                            (errors?.intellifloIntegrationLegalEntitiesInput?.[index] as FormikErrors<IntellifloIntegrationLegalType>)?.legalEntity?.customerName
                                          }
                                          InputLabelProps={{ shrink: true }}
                                          disabled={!canModifyTenant || (!canUpdateIOtenant || values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.status === LegalEntityStatus.INACTIVE)}

                                        />
                                      </FormControl>
                                    </Grid>

                                    <Grid item md={3} xs={12}>
                                      <FormControl variant="standard" style={{ width: "100%" }}>
                                        <TextField
                                          fullWidth
                                          variant="outlined"
                                          type="text"
                                          label="Customer Id"
                                          onBlur={handleBlur}
                                          onChange={handleChange}
                                          value={values.intellifloIntegrationLegalEntitiesInput?.[index]?.legalEntity?.customerId}
                                          name={`intellifloIntegrationLegalEntitiesInput[${index}].legalEntity.customerId`}
                                          error={
                                            !!touched?.intellifloIntegrationLegalEntitiesInput?.[index]?.legalEntity?.customerId &&
                                            !!(errors?.intellifloIntegrationLegalEntitiesInput?.[index] as FormikErrors<IntellifloIntegrationLegalType>)?.legalEntity?.customerId
                                          }
                                          helperText={!!touched?.intellifloIntegrationLegalEntitiesInput?.[index]?.legalEntity?.customerId &&
                                            (errors?.intellifloIntegrationLegalEntitiesInput?.[index] as FormikErrors<IntellifloIntegrationLegalType>)?.legalEntity?.customerId
                                          }
                                          InputLabelProps={{ shrink: true }}
                                          disabled={!canModifyTenant || (!canUpdateIOtenant || values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.status === LegalEntityStatus.INACTIVE)}

                                        />
                                      </FormControl>
                                    </Grid>

                                    <Grid item md={3} xs={12}>
                                      <FormControl variant="standard" style={{ width: "100%" }}>
                                        <TextField
                                          fullWidth
                                          variant="outlined"
                                          type="text"
                                          label="External Group Id"
                                          onBlur={handleBlur}
                                          onChange={handleChange}
                                          value={values.intellifloIntegrationLegalEntitiesInput?.[index]?.legalEntity?.groupId}
                                          name={`intellifloIntegrationLegalEntitiesInput[${index}].legalEntity.groupId`}
                                          error={
                                            !!touched?.intellifloIntegrationLegalEntitiesInput?.[index]?.legalEntity?.groupId &&
                                            !!(errors?.intellifloIntegrationLegalEntitiesInput?.[index] as FormikErrors<IntellifloIntegrationLegalType>)?.legalEntity?.groupId
                                          }
                                          helperText={!!touched?.intellifloIntegrationLegalEntitiesInput?.[index]?.legalEntity?.groupId &&
                                            (errors?.intellifloIntegrationLegalEntitiesInput?.[index] as FormikErrors<IntellifloIntegrationLegalType>)?.legalEntity?.groupId
                                          }
                                          InputLabelProps={{ shrink: true }}
                                          disabled={!canModifyTenant || (!canUpdateIOtenant || values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.status === LegalEntityStatus.INACTIVE)}

                                        />
                                      </FormControl>
                                    </Grid>

                                    <Grid item md={3} xs={12}>
                                      <FormControl variant="standard" style={{ width: "100%" }}>
                                        <TextField
                                          fullWidth
                                          variant="outlined"
                                          type="text"
                                          label="FCA Reference"
                                          onBlur={handleBlur}
                                          onChange={handleChange}
                                          value={values.intellifloIntegrationLegalEntitiesInput?.[index]?.legalEntity?.fcaRefNo}
                                          name={`intellifloIntegrationLegalEntitiesInput[${index}].legalEntity.fcaRefNo`}
                                          error={
                                            !!touched?.intellifloIntegrationLegalEntitiesInput?.[index]?.legalEntity?.fcaRefNo &&
                                            !!(errors?.intellifloIntegrationLegalEntitiesInput?.[index] as FormikErrors<IntellifloIntegrationLegalType>)?.legalEntity?.fcaRefNo
                                          }
                                          helperText={!!touched?.intellifloIntegrationLegalEntitiesInput?.[index]?.legalEntity?.fcaRefNo &&
                                            (errors?.intellifloIntegrationLegalEntitiesInput?.[index] as FormikErrors<IntellifloIntegrationLegalType>)?.legalEntity?.fcaRefNo
                                          }
                                          InputLabelProps={{ shrink: true }}
                                          disabled={!canModifyTenant || (!canUpdateIOtenant || values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.status === LegalEntityStatus.INACTIVE)}
                                        />

                                      </FormControl>
                                    </Grid>
                                  </Grid>

                                  <Grid item md={6} xs={10} style={{ textAlign: "left", alignContent: "center" }}>
                                    <FormControl variant="standard" style={{ width: "100%" }}>
                                      <FormControlLabel
                                        name={`intellifloIntegrationLegalEntitiesInput[${index}].integrationSettings.settings.wealthLinkModelSync.enabled`}
                                        control={<Checkbox
                                          checked={
                                            values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.settings?.wealthLinkModelSync?.enabled ||
                                            false
                                          }
                                          onChange={handleChange} />}
                                        label="Model Sync Enabled"
                                        labelPlacement="end"
                                        disabled={!canModifyTenant || (!canUpdateIOtenant || values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.status === LegalEntityStatus.INACTIVE)}
                                      />
                                    </FormControl>
                                  </Grid>
                                  <Grid item md={6} xs={10} style={{ textAlign: "left", alignContent: "center" }}>
                                    <FormControl variant="standard" style={{ width: "100%" }}>
                                      <FormControlLabel
                                        name={`intellifloIntegrationLegalEntitiesInput[${index}].integrationSettings.settings.wealthLinkIncomeStatement.enabled`}
                                        control={<Checkbox
                                          checked={
                                            values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.settings?.wealthLinkIncomeStatement?.enabled || false
                                          } onChange={handleChange} />}
                                        label="Income Statement"
                                        labelPlacement="end"
                                        disabled={!canModifyTenant || (!canUpdateIOtenant || values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.status === LegalEntityStatus.INACTIVE)}
                                      />
                                    </FormControl>
                                  </Grid>
                                  <Grid item md={6} xs={10} style={{ textAlign: "left", alignContent: "center" }}>
                                    <FormControl variant="standard" style={{ width: "100%" }}>
                                      <FormControlLabel
                                        name={`intellifloIntegrationLegalEntitiesInput[${index}].integrationSettings.settings.allowClientImport`}
                                        control={<Checkbox
                                          checked={
                                            values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.settings?.allowClientImport || false
                                          } onChange={handleChange} />}
                                        label="Allow Client Import"
                                        labelPlacement="end"
                                        disabled={values.intellifloTenantInput?.type === IntegrationService.IntegrationType.GATEWAY || !canModifyTenant || !canUpdateIOtenant || values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.status === LegalEntityStatus.INACTIVE}
                                      />
                                    </FormControl>
                                  </Grid>
                                  <Grid item md={6} xs={10} style={{ textAlign: "left", alignContent: "center" }}>
                                    <FormControl variant="standard" style={{ width: "100%" }}>
                                      <FormControlLabel
                                        name={`intellifloIntegrationLegalEntitiesInput[${index}].integrationSettings.settings.allowClientRelationshipImport`}
                                        control={<Checkbox
                                          checked={
                                            values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.settings?.allowClientRelationshipImport || false
                                          }
                                          onChange={handleChange} />}
                                        label="Allow Client Relationship Import"
                                        labelPlacement="end"
                                        disabled={values.intellifloTenantInput?.type === IntegrationService.IntegrationType.GATEWAY || !canModifyTenant || !canUpdateIOtenant || values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.status === LegalEntityStatus.INACTIVE}
                                      />
                                    </FormControl>
                                  </Grid>
                                  <Grid item md={6} xs={10} style={{ textAlign: "left", alignContent: "center" }}>
                                    <FormControl variant="standard" style={{ width: "100%" }}>
                                      <FormControlLabel
                                        name={`intellifloIntegrationLegalEntitiesInput[${index}].integrationSettings.settings.allowDocumentUpload`}
                                        control={<Checkbox
                                          checked={
                                            values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.settings?.allowDocumentUpload || false
                                          }
                                          onChange={handleChange} />}
                                        label="Allow Document Upload"
                                        labelPlacement="end"
                                        disabled={values.intellifloTenantInput?.type === IntegrationService.IntegrationType.GATEWAY || values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.status === LegalEntityStatus.INACTIVE}
                                      />
                                    </FormControl>
                                  </Grid>
                                  <Grid item md={6} xs={10} style={{ textAlign: "left", alignContent: "center" }}>
                                    <FormControl variant="standard" style={{ width: "100%" }}>
                                      <FormControlLabel
                                        name={`intellifloIntegrationLegalEntitiesInput[${index}].integrationSettings.settings.allowWealthLinkBulkValuation`}
                                        control={<Checkbox
                                          checked={
                                            values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.settings?.allowWealthLinkBulkValuation || false
                                          }

                                          onChange={handleChange} />}
                                        label="Allow WealthLink Bulk Valuation"
                                        labelPlacement="end"
                                        disabled={!canModifyTenant || !canUpdateIOtenant || values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.status === LegalEntityStatus.INACTIVE}
                                      />
                                    </FormControl>
                                  </Grid>

                                  {canModifyTenant && values?.intellifloIntegrationLegalEntitiesInput.length > 1 && (
                                    <Grid item md={12} xs={12}>
                                      <Box width="100%" display="flex" justifyContent="flex-end" gap={1}>
                                        <Button
                                          type="button"
                                          variant="contained"
                                          sx={{ maxHeight: "38px" }}
                                          startIcon={values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.status === LegalEntityStatus.INACTIVE ? <CheckBox /> : <DisabledByDefault />}
                                          color={values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.status === LegalEntityStatus.INACTIVE ? "success" : "error"}
                                          onClick={() => {
                                            const entity = values.intellifloIntegrationLegalEntitiesInput?.[index];
                                            const entityTenantId = values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.tenantId;
                                            if (entity && id) {
                                              const customerId = entity.legalEntity.customerId;
                                              const currentStatus = entity.integrationSettings.status;
                                              if (currentStatus !== LegalEntityStatus.INACTIVE) {
                                                setSelectedEntity({ customerId, status: currentStatus, tenantId: entityTenantId });
                                                setDialogOpen(true);
                                              } else {
                                                toggleLegalEntityStatus(id, customerId, LegalEntityStatus.ACTIVE, true);
                                              }
                                            }
                                          }}
                                        >
                                          {values.intellifloIntegrationLegalEntitiesInput?.[index]?.integrationSettings?.status === LegalEntityStatus.INACTIVE ? "Activate" : "Disable"}
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="outlined"
                                          sx={{ maxHeight: "38px" }}
                                          color="primary"
                                          onClick={() => {
                                            arrayHelpers.remove(index);
                                          }}
                                        >
                                            Remove
                                        </Button>
                                      </Box>
                                    </Grid>
                                  )}
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                        </Grid>
                        {canUpdateIOtenant && (
                          <Box width="100%" display="flex" mt="5px" justifyContent="flex-end">
                            <Button
                              color="primary"
                              style={{ marginTop: 10, width: "150px" }}
                              variant="contained"
                              type="button"
                              onClick={() =>
                                arrayHelpers.push({
                                  integrationSettings: {
                                    id: "",
                                    settings: {
                                      allowClientImport: false,
                                      allowClientRelationshipImport: false,
                                      allowDocumentUpload: false,
                                      allowWealthLinkBulkValuation: false,
                                      type: "",
                                      wealthLinkIncomeStatement: { enabled: false },
                                      wealthLinkModelSync: { enabled: false },
                                    },
                                  },
                                  legalEntity: {
                                    customerId: "",
                                    groupId: "",
                                    fcaRefNo: "",
                                  },
                                })
                              }
                            >
                                Add More
                            </Button>
                          </Box>
                        )}

                      </>
                    )}
                  />
                </div>
              </Box>
              <Watcher />
            </Box>
            <Dialog
              open={dialogOpen}
              onClose={() => setDialogOpen(false)}
            >
              <DialogTitle>
                <Typography variant="h4">
                   Are you sure you want to disable <b>{selectedEntity?.customerId}</b>?
                </Typography>
              </DialogTitle>
              <DialogContent>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={stopIntegration}
                      onChange={(e) => setStopIntegration(e.target.checked)}
                      name="stopIntegration"
                    />
                  }
                  label="Stop integration connection as well"
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDialogOpen(false)} variant="outlined">Cancel</Button>
                <Button
                  onClick={async () => {
                    if (selectedEntity && id) {
                      await toggleLegalEntityStatus(selectedEntity.tenantId, selectedEntity.customerId, LegalEntityStatus.INACTIVE, stopIntegration);
                    }
                    setDialogOpen(false);
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={24} /> : "Yes"}
                </Button>
              </DialogActions>
            </Dialog>
            {
              <Box width="100%" display="flex" gap={2} mt={2}>
                {canUpdateIOtenant && (
                  <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                  >
                      Save
                  </Button>
                )}
                { isEdit && (
                  <Button
                    color="error"
                    variant="contained"
                    type="button"
                    onClick={handleOpenConfirmationDialog}
                    startIcon={<ActionIcon />}
                  >
                    {actionButtonLabel}
                  </Button>)}
              </Box>
            }
            <Dialog open={isConfirmDialogOpen} onClose={handleCloseConfirmationDialog} fullWidth maxWidth="xs">
              <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {dialogInstruction}
                </Typography>
                <TextField
                  fullWidth
                  autoFocus
                  label="Confirmation"
                  value={confirmationValue}
                  onChange={event => setConfirmationValue(event.target.value)}
                />
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleCloseConfirmationDialog}>
                    Cancel
                </Button>
                <Button
                  color="error"
                  variant="contained"
                  onClick={handleConfirmAction}
                  disabled={!isConfirmationValid || isDisableOrDeleteLoading}
                >
                  {confirmButtonLabel}
                </Button>
              </DialogActions>
            </Dialog>
          </form>;
        }}
      </Formik>
    </>
  );
}


export default EditIntegration;


const Watcher: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<CreateIntellifloTenantInput>();

  useEffect(() => {
    const integrationType = values.intellifloTenantInput?.type;

    if (integrationType === IntegrationService.IntegrationType.GATEWAY) {
      setFieldValue("intellifloIntegrationInput.settings.allowClientImport", false);
      setFieldValue("intellifloIntegrationInput.settings.allowClientRelationshipImport", false);
      setFieldValue("intellifloIntegrationInput.settings.allowDocumentUpload", false);
    }
  }, [values.intellifloTenantInput?.type, setFieldValue]);

  return null;
};

